import React, { useState, useEffect } from 'react';
import { Warning, ArrowRight, ChevronUp, ChevronDown } from '../../shared/components/Icons';
import { InfoTooltip } from '../../shared/components/InfoTooltip';
import { formatDate } from '../../shared/utils/dateUtils';
import { api } from '../../shared/utils/api';
import { confColor } from './confColorUtils';

/* ── Tiny SVG sparkline ─────────────────────────────────────────────── */
function Sparkline({ data, color, width = 50, height = 20 }) {
  if (!data || data.length < 2) return null;
  const vals = data.map(d => d.confidence).filter(v => v !== null);
  if (vals.length < 2) return null;
  const min = Math.min(...vals), max = Math.max(...vals);
  const range = max - min || 0.01;
  const points = vals.map((v, i) => {
    const x = (i / (vals.length - 1)) * width;
    const y = height - ((v - min) / range) * (height - 4) - 2;
    return `${x},${y}`;
  }).join(' ');
  return (
    <svg width={width} height={height} style={{ display: 'block' }}>
      <polyline points={points} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

/* ── Diagnostic card for expanded topic row ──────────────────────── */
function DiagCard({ label, children }) {
  return (
    <div className="diag-card">
      <div className="diag-label">{label}</div>
      {children}
    </div>
  );
}

/* ── Diagnostic zone: shows everything the app knows about a topic ── */
export function DiagnosticZone({ diagnostic, topic, onNavigateToSRA }) {
  if (!diagnostic) return <div className="diag-loading">Loading</div>;

  const hist = diagnostic.confidence_history || [];
  const mcq = diagnostic.mcq_stats;
  const sra = diagnostic.sra_stats;
  const recall = diagnostic.recall_stats;
  const lastUpdated = diagnostic.last_updated;
  const hasAnyData = hist.length > 0 || mcq || sra || recall;

  if (!hasAnyData) {
    return (
      <div className="diag-empty">
        No data for this topic. Set your confidence below{onNavigateToSRA && <> or <button className="diag-link" onClick={() => onNavigateToSRA(topic.topic)}>try the SRA questions</button></>}.
      </div>
    );
  }

  // Sparkline data — reverse so oldest is first
  const sparkData = [...hist].reverse();
  const latestConf = topic.confidence;
  const confPct = latestConf !== null ? Math.round(latestConf * 100) : null;
  const cColor = confColor(latestConf);

  return (
    <div className="diag-zone">
      {/* Card 1: Confidence trend */}
      {confPct !== null && (
        <DiagCard label={<>Confidence<InfoTooltip text="How confident you feel about this topic. Adjust the slider to update." /></>}>
          <div className="diag-row">
            <span className="diag-value" style={{ color: cColor }}>{confPct}%</span>
            <Sparkline data={sparkData} color={cColor} />
          </div>
          {hist.length > 1 && (
            <div className="diag-sub">{hist.length} updates</div>
          )}
        </DiagCard>
      )}

      {/* Card 2: MCQ accuracy */}
      {mcq && (
        <DiagCard label={<>MCQ accuracy<InfoTooltip text="Your accuracy on MCQ questions mapped to this topic area." /></>}>
          <span className="diag-value" style={{ color: mcq.accuracy >= 70 ? 'var(--green)' : mcq.accuracy >= 50 ? 'var(--amber)' : 'var(--red)' }}>
            {mcq.accuracy}%
          </span>
          <div className="diag-sub">from {mcq.attempted} question{mcq.attempted !== 1 ? 's' : ''}</div>
        </DiagCard>
      )}

      {/* Card 3: SRA accuracy */}
      {sra && (
        <DiagCard label={<>SRA questions<InfoTooltip text="220 official sample questions from the SRA, split between FLK1 and FLK2. Your results map to topics and feed into your priority dashboard." /></>}>
          <span className="diag-value" style={{ color: sra.accuracy >= 70 ? 'var(--green)' : sra.accuracy >= 50 ? 'var(--amber)' : 'var(--red)' }}>
            {sra.correct} of {sra.attempted}
          </span>
          <div className="diag-sub">{sra.accuracy}% correct · {sra.total - sra.attempted} remaining</div>
        </DiagCard>
      )}

      {/* Card 4: Last recall */}
      {recall && (
        <DiagCard label="Last recall">
          <span className="diag-value">{recall.self_rating || '—'}</span>
          <div className="diag-sub">{recall.days_since === 0 ? 'today' : recall.days_since === 1 ? 'yesterday' : `${recall.days_since} days ago`}</div>
        </DiagCard>
      )}

      {/* Card 5: Last updated */}
      {lastUpdated && (
        <DiagCard label="Last updated">
          <span className="diag-value diag-value-sm">{formatDate(lastUpdated, 'full')}</span>
          {hist.length > 0 && hist[0].status && (
            <div className="diag-sub">{hist[0].status}</div>
          )}
        </DiagCard>
      )}
    </div>
  );
}

export const DEFAULT_STATUSES = ['Not started', 'Learning', 'Practised', 'Confident', 'Needs review'];

const DEFAULT_STATUS_COLORS = ['#F3F4F6', '#DBEAFE', '#FEF3C7', '#D1FAE5', '#FEE2E2'];

export function getStatusColor(status, allStatuses) {
  if (!allStatuses || !status) return null;
  const idx = allStatuses.findIndex(s =>
    typeof s === 'string' ? s === status : s.label === status
  );
  if (idx < 0) return null;
  const item = allStatuses[idx];
  if (typeof item === 'object' && item.color) return item.color;
  return DEFAULT_STATUS_COLORS[idx] || '#F3F4F6';
}


const SRA_ACCOUNTS_LINKS = [
  { title: 'SRA Accounts Rules 2019 (live)', url: 'https://www.sra.org.uk/solicitors/standards-regulations/accounts-rules/', desc: 'The full authoritative rules. Always current.' },
  { title: 'SRA — Keeping client money safe', url: 'https://www.sra.org.uk/solicitors/guidance/keeping-client-money-safe/', desc: 'Guidance on client account obligations' },
  { title: 'SRA — Reporting concerns about client money', url: 'https://www.sra.org.uk/consumers/problems/report-solicitor/', desc: 'SRA reporting framework' },
  { title: 'SRA Standards & Regulations hub', url: 'https://www.sra.org.uk/solicitors/standards-regulations/', desc: 'Full standards and regulations index' },
];

export function SRALinks() {
  const [open, setOpen] = useState(false);
  return (
    <div className="sra-links-section">
      <button className="history-toggle" onClick={()=>setOpen(o=>!o)}>
        {open?<ChevronUp size={10} />:<ChevronDown size={10} />} SRA source materials
      </button>
      {open&&(
        <div className="sra-links-list">
          <div className="sra-links-warning">
            These link to the SRA website. Current version as published. Do not rely on notes or third-party summaries for rules that may have changed.
          </div>
          {SRA_ACCOUNTS_LINKS.map(l=>(
            <a key={l.url} href={l.url} target="_blank" rel="noopener noreferrer" className="sra-link-item">
              <span className="sra-link-title">{l.title}</span>
              <span className="sra-link-desc">{l.desc}</span>
              <span className="sra-link-arrow"><ArrowRight size={12} /></span>
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

export function HistoryLog({ topicId }) {
  const [history, setHistory] = useState(null);
  useEffect(()=>{ api(`/api/topics/${topicId}/history`).then(setHistory); },[topicId]);
  if (!history) return <div style={{fontSize:11,color:'var(--muted)',padding:'4px 0'}}>Loading</div>;
  if (history.length===0) return <div style={{fontSize:11,color:'var(--hint)',padding:'4px 0'}}>No history recorded</div>;
  return (
    <div className="history-log">
      {history.map(h=>(
        <div key={h.id} className="history-row">
          <span className="history-date">{(() => {
            try {
              const d = new Date(h.changed_at.replace(' ', 'T'));
              return d.toLocaleDateString('en-GB', {day:'numeric',month:'short',year:'numeric'}) + ' ' + d.toLocaleTimeString('en-GB',{hour:'2-digit',minute:'2-digit'});
            } catch { return h.changed_at; }
          })()}</span>
          <span className="history-status">{h.status||'—'}</span>
          <span className="history-conf" style={{color:confColor(h.confidence)}}>
            {h.confidence!==null?Math.round(h.confidence*100)+'%':'—'}
          </span>
        </div>
      ))}
    </div>
  );
}

export function MCQHistory({ topicId }) {
  const [results, setResults] = useState(null);
  useEffect(()=>{
    api('/api/mcq-results').then(all=>{
      setResults(all.filter(r=>r.topic_id===topicId));
    });
  },[topicId]);

  if (!results) return <div style={{fontSize:11,color:'var(--muted)',padding:'4px 0'}}>Loading</div>;
  if (results.length===0) return <div style={{fontSize:11,color:'var(--hint)',padding:'4px 0'}}>No MCQ results for this topic. Import a session to populate this view.</div>;

  const withPct = results.filter(r => r.score_pct != null);
  const overallPct = withPct.length > 0
    ? Math.round(withPct.reduce((s,r) => s + r.score_pct, 0) / withPct.length)
    : results.some(r => r.attempted > 0)
      ? Math.round(results.reduce((s,r) => s + r.correct, 0) / results.reduce((s,r) => s + r.attempted, 0) * 100)
      : null;
  const overallColor = overallPct == null ? 'var(--muted)' : overallPct>=70?'var(--green)':overallPct>=50?'var(--amber)':'var(--red)';

  return (
    <div className="mcq-history">
      <div className="mcq-history-summary">
        <span style={{fontSize:12,color:'var(--muted)'}}>Overall accuracy: </span>
        <span style={{fontFamily:'var(--mono)',fontSize:13,fontWeight:500,color:overallColor}}>
          {overallPct != null ? overallPct + '%' : '—'}
        </span>
        <span style={{fontSize:11,color:'var(--muted)'}}> across {results.length} session{results.length!==1?'s':''}</span>
      </div>
      <div className="mcq-history-rows">
        {results.map(r=>{
          const pct = r.score_pct != null ? r.score_pct : (r.attempted > 0 ? Math.round(r.correct/r.attempted*100) : null);
          const c = pct == null ? 'var(--muted)' : pct>=70?'var(--green)':pct>=50?'var(--amber)':'var(--red)';
          const dateStr = (() => { try { return new Date(r.session_date).toLocaleDateString('en-GB',{day:'2-digit',month:'2-digit',year:'numeric'}); } catch { return r.session_date; } })();
          return (
            <div key={r.id} className="mcq-history-row" style={{gridTemplateColumns:'90px 1fr 44px 1fr'}}>
              <span className="mcq-history-date">{dateStr}</span>
              <div className="mcq-history-bar-wrap">
                <div className="mcq-history-bar" style={{width:(pct||0)+'%',background:c}}/>
              </div>
              <span className="mcq-history-pct" style={{color:c}}>{pct != null ? pct+'%' : '—'}</span>
              {r.sub_subtopic && r.sub_subtopic !== r.topic_name ? (
                <span style={{fontSize:10,color:'var(--muted)',fontStyle:'italic',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                  {r.source}: "{r.sub_subtopic}"
                </span>
              ) : (
                <span style={{fontSize:10,color:'var(--hint)'}}>{r.source}</span>
              )}
            </div>
          );
        })}
      </div>
      {results.some(r => r.score_pct != null && r.score_pct < 70) && (
        <div className="mcq-history-weak-note">
          <Warning size={11} /> Some sessions below 70%. Consider recall practice on this topic.
        </div>
      )}
    </div>
  );
}
